import { colors } from '../../colors';
import type { ToggleSize, ToggleSizeTokens, ToggleStyleProps } from './types';

const DEFAULT_COLORS = {
  primary: colors.primary[300],
  primaryHover: colors.primary[600],
  uncheckedTrack: colors.gray[200],
  uncheckedTrackHover: colors.gray[300],
  gray300: colors.gray[300],
  gray500: colors.gray[500],
  gray700: colors.gray[700],
  white: colors.white,
  transparent: 'transparent',
};

export const getToggleSizeTokens = (size: ToggleSize): ToggleSizeTokens => {
  switch (size) {
    case 'small':
      return {
        trackWidth: 36,
        trackHeight: 20,
        thumb: 16,
        translate: 16,
      };
    case 'large':
      return {
        trackWidth: 60,
        trackHeight: 34,
        thumb: 30,
        translate: 26,
      };
    case 'medium':
    default:
      return {
        trackWidth: 48,
        trackHeight: 26,
        thumb: 22,
        translate: 22,
      };
  }
};

export const getToggleTrackStyles = (
  checked: boolean,
  disabled: boolean,
  tokens: ToggleSizeTokens,
  styleProps?: ToggleStyleProps
): string => {
  const { trackBackgroundColor, checkedTrackBackgroundColor, focusRingColor, disabledOpacity } =
    styleProps ?? {};

  const backgroundColor = checked
    ? (checkedTrackBackgroundColor ?? DEFAULT_COLORS.primary)
    : (trackBackgroundColor ?? DEFAULT_COLORS.uncheckedTrack);

  return `
    position: relative;
    width: ${tokens.trackWidth}px;
    height: ${tokens.trackHeight}px;
    background-color: ${backgroundColor};
    border: none;
    border-radius: ${tokens.trackHeight}px;
    transition: all 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    opacity: ${disabled ? (disabledOpacity ?? 0.6) : 1};
    box-sizing: border-box;
    box-shadow: ${checked ? 'none' : 'inset 0 1px 3px rgba(0, 0, 0, 0.15)'};

    &:hover {
      transform: ${disabled ? 'none' : 'scale(1.02)'};
    }

    &:focus-within {
      outline: 2px solid ${focusRingColor ?? DEFAULT_COLORS.primary}40;
      outline-offset: 2px;
      box-shadow: ${checked ? '0 0 0 4px rgba(59, 130, 246, 0.15)' : 'inset 0 1px 3px rgba(0, 0, 0, 0.15), 0 0 0 4px rgba(59, 130, 246, 0.15)'};
    }
  `;
};

export const getToggleThumbStyles = (
  checked: boolean,
  disabled: boolean,
  tokens: ToggleSizeTokens,
  styleProps?: ToggleStyleProps
): string => {
  const { thumbBackgroundColor } = styleProps ?? {};

  const padding = 2;
  const translateX = checked ? tokens.trackWidth - tokens.thumb - padding * 2 : 0;

  return `
    position: absolute;
    top: 2px;
    left: 2px;
    width: ${tokens.thumb}px;
    height: ${tokens.thumb}px;
    background-color: ${thumbBackgroundColor ?? DEFAULT_COLORS.white};
    border-radius: 50%;
    transition: all 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
    transform: translateX(${translateX}px);
    box-shadow: ${
      checked
        ? '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
        : '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)'
    };
    box-sizing: border-box;

    &:hover {
      transform: translateX(${translateX}px) ${disabled ? '' : 'scale(1.1)'};
    }
  `;
};

export const getToggleLabelStyles = (disabled: boolean, styleProps?: ToggleStyleProps): string => {
  const { labelColor, disabledOpacity } = styleProps ?? {};

  return `
    color: ${labelColor ?? 'inherit'};
    font-weight: 500;
    opacity: ${disabled ? (disabledOpacity ?? 0.5) : 1};
  `;
};

export const getToggleDescriptionStyles = (
  _disabled: boolean,
  styleProps?: ToggleStyleProps
): string => {
  const { descriptionColor, disabledOpacity } = styleProps ?? {};

  return `
    color: ${descriptionColor ?? DEFAULT_COLORS.gray500};
    font-size: 14px;
    opacity: ${_disabled ? (disabledOpacity ?? 0.5) : 1};
  `;
};